import { useLayoutEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import { VirtualBackgroundType } from '../../../../../../common/types/settings';
import { useActiveDeviceStream } from '../../device-manager/hooks';
import { getVideoInputError } from '../../device-manager/selectors';
import { deviceManager } from '../../goloom-sdk';
import { FacingMode } from '../../settings';

interface UseCurrentVideoStreamParams {
    deviceIdOrFacingMode?: FacingMode | string;
    virtualBackgroundType?: VirtualBackgroundType;
}

export const useCurrentVideoStream = ({
    deviceIdOrFacingMode,
    virtualBackgroundType,
}: UseCurrentVideoStreamParams = {}) => {
    const videoInputError = useSelector(getVideoInputError);

    const videoRef = useRef<HTMLVideoElement>(null);

    const [mediaStream, setMediaStream] = useState<Maybe<MediaStream>>();
    const [isVideoReady, setIsVideoReady] = useState<boolean>(false);
    const activeDeviceMediaStream = useActiveDeviceStream('videoinput', true);

    useLayoutEffect(() => {
        if (videoRef.current) {
            videoRef.current.srcObject = activeDeviceMediaStream ?? mediaStream ?? null;
        }
    }, [mediaStream, activeDeviceMediaStream]);

    useLayoutEffect(() => {
        if (deviceManager.videoStatus) {
            return;
        }
        let isIgnored = false;
        const cameraStreamPromise = deviceManager.getLocalPreviewCameraStream(deviceIdOrFacingMode);

        cameraStreamPromise.then((mediaStream) => {
            if (isIgnored) {
                return;
            }
            setMediaStream(mediaStream ?? undefined);
        });
        return () => {
            isIgnored = true;
            deviceManager.stopPreviewVideoStream();
        };
    }, [deviceIdOrFacingMode, virtualBackgroundType]);

    useLayoutEffect(() => {
        const currentVideoRef = videoRef.current;
        if (currentVideoRef) {
            let timer: Maybe<ReturnType<typeof setTimeout>>;

            const onCanPlayThrough = () => {
                timer = setTimeout(() => setIsVideoReady(true), 100);
            };

            currentVideoRef.addEventListener('canplaythrough', onCanPlayThrough);

            return () => {
                currentVideoRef.removeEventListener('canplaythrough', onCanPlayThrough);
                if (timer) {
                    clearTimeout(timer);
                }
            };
        }
    }, []);

    const withMirror = deviceIdOrFacingMode !== FacingMode.ENVIRONMENT;

    return { videoRef, isVideoReady, videoInputError, withMirror, setIsVideoReady };
};
